import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { showApiErrorToast } from '@/utils/errors';
import {
  amendDonation,
  decideDonation,
  recommendDonation,
  rejectRecommendation,
  verifyMis,
} from '../api/marketPromotion.api';
import { DONATION_DECISION } from '../constants/marketPromotion.constants';
import type {
  AmendDonationPayload,
  DecideDonationPayload,
  RecommendDonationPayload,
  RejectRecommendationPayload,
  VerifyMisPayload,
} from '../types/marketPromotion.types';
import { useInvalidateMarketPromotion } from './useInvalidateMarketPromotion';

export function useDecideDonation(onSuccess?: () => void) {
  const invalidate = useInvalidateMarketPromotion();

  return useMutation({
    mutationFn: ({ detailId, payload }: { detailId: number; payload: DecideDonationPayload }) =>
      decideDonation(detailId, payload),
    onSuccess: (_data, variables) => {
      invalidate();
      toast.success(
        variables.payload.approveTypeNo === DONATION_DECISION.APPROVED
          ? 'Donation approved'
          : 'Donation rejected',
      );
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to record the decision'),
  });
}

export function useRecommendDonation(onSuccess?: () => void) {
  const invalidate = useInvalidateMarketPromotion();

  return useMutation({
    mutationFn: ({ detailId, payload }: { detailId: number; payload: RecommendDonationPayload }) =>
      recommendDonation(detailId, payload),
    onSuccess: () => {
      invalidate();
      toast.success('Donation recommended');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to recommend the donation'),
  });
}

export function useRejectRecommendation(onSuccess?: () => void) {
  const invalidate = useInvalidateMarketPromotion();

  return useMutation({
    mutationFn: ({
      detailId,
      payload,
    }: {
      detailId: number;
      payload: RejectRecommendationPayload;
    }) => rejectRecommendation(detailId, payload),
    onSuccess: () => {
      invalidate();
      toast.success('Donation rejected');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to reject the donation'),
  });
}

export function useAmendDonation(onSuccess?: () => void) {
  const invalidate = useInvalidateMarketPromotion();

  return useMutation({
    mutationFn: ({ detailId, payload }: { detailId: number; payload: AmendDonationPayload }) =>
      amendDonation(detailId, payload),
    onSuccess: () => {
      invalidate();
      toast.success('Donation corrected');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to correct the donation'),
  });
}

export function useVerifyMis(onSuccess?: () => void) {
  const invalidate = useInvalidateMarketPromotion();

  return useMutation({
    mutationFn: ({ detailId, payload }: { detailId: number; payload: VerifyMisPayload }) =>
      verifyMis(detailId, payload),
    onSuccess: () => {
      invalidate();
      toast.success('MIS verification saved');
      onSuccess?.();
    },
    onError: (error) => showApiErrorToast(error, 'Failed to save the MIS verification'),
  });
}
